import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  FeedListItem,
  FeedListInfinitySpinner,
  LoaderDashboard,
  DashboardHeading,
  Breadcrumbs,
} from 'components';
import { IntegrationFeedNoResults } from 'screens/IntegrationScreen';
import { IntegrationFeedQuery, handleFetchMoreError, criticalFeedError } from 'services/apollo';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { useFeatureFlagState } from 'context';
import { trackingIntegrationFeed } from 'services/analytics';
import styles from './IntegrationFeed.module.scss';
import { useQueryLifecycle } from 'hooks/useQueryLifecycle';

export const IntegrationFeed = () => {
  const { groupsFeatureFlag } = useFeatureFlagState();
  const { integrationType } = useParams();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { error, data, fetchMore, networkStatus } = useQuery(IntegrationFeedQuery, {
    variables: { cursor: '', integrationType, includeGroups: groupsFeatureFlag },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { isCached: showingCachedData } = useQueryLifecycle(networkStatus);

  useEffect(() => {
    if (!showingCachedData) {
      const appName = data?.integrationSettings?.title;
      const totalItems = data?.me.activityFeed?.pageInfo?.total;
      trackingIntegrationFeed(appName, totalItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationType, showingCachedData]);

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.me.activityFeed.pageInfo;
    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.me.activityFeed.nodes = [
            ...prevResult.me.activityFeed.nodes,
            ...fetchMoreResult.me.activityFeed.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => {
        return response;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined) return <LoaderDashboard layout="IntegrationFeed" />;

  if (error && criticalFeedError(error)) {
    return <LoaderDashboard layout="IntegrationFeed" />;
  }

  if (data.me.activityFeed.integrationType !== integrationType) {
    return <LoaderDashboard layout="IntegrationFeed" />;
  }

  if (!data.me.activityFeed.nodes.length)
    return <IntegrationFeedNoResults integrationType={integrationType} />;

  return (
    <>
      <Breadcrumbs
        links={[
          {
            label: data.integrationSettings.title,
            to: '/integration/' + integrationType,
            reload: true,
          },
        ]}
      />
      <div className={styles.Heading}>
        <DashboardHeading level={1}>Activity Feed</DashboardHeading>
      </div>
      {data.me.activityFeed.nodes.map((node: any, key: number) => (
        <FeedListItem
          nodeId={node.id}
          nodeType={node.__typename}
          key={key}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          trackIndex={key}
          trackFeedType={'IntegrationFeed'}
          trackTotalItems={data.me.activityFeed.pageInfo.total}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
        />
      ))}
      {data.me.activityFeed.pageInfo.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
    </>
  );
};
